/** What the redeem screens share: the status select that brakes a pool, the
 *  desk settings (account, automatic sending, daily cap), the new-app dialog
 *  and the expiry D-day. */

import { useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api, type CreateRedeemAppPayload, type RedeemApp, type RedeemAppStatus, type RedeemPlatform } from '@/api/client'
import { useInvalidateRedeem } from '@/lib/redeem'
import { useTargets } from '@/lib/queries'
import { STATUS, TONE_CLASS } from '@/lib/status'
import { daysUntil, formatDday } from '@/lib/time'
import { cn } from '@/lib/utils'
import { Button, ConfirmDialog, Dialog, Field, Input, Select, StatusDot, errorText, useToast } from '@/ui'

export const APP_PLATFORM: Record<RedeemPlatform, string> = { ios: 'iOS', mac: 'Mac', play: 'Play', web: 'Web' }

/** Keeps a click or key inside a control from also opening the row it sits in. */
export function Stop({ children, className }: { children: ReactNode; className?: string }) {
  const stop = (e: MouseEvent | KeyboardEvent) => e.stopPropagation()
  return (
    <span className={className} onClick={stop} onKeyDown={stop}>
      {children}
    </span>
  )
}

/** A code expiry with how far off it is. A code handed out on its last day is
 *  a support ticket, so inside two weeks it turns amber, past it red. */
export function Expiry({ date, className }: { date?: string; className?: string }) {
  const d = daysUntil(date)
  if (d === null) return <span className="text-muted-foreground">—</span>
  const tone = d < 0 ? TONE_CLASS.failed.text : d <= 14 ? TONE_CLASS.publishing.text : ''
  return (
    <span className={cn('whitespace-nowrap', className)}>
      {date} <span className={cn('text-meta', tone || 'text-muted-foreground')}>{formatDday(d)}</span>
    </span>
  )
}

/** What a resume or reopen tells the operator when it took passed slots
 *  back: those posts wait for a new time instead of all going out at once. */
export function unslottedNote(n?: number) {
  return n ? ` 멈춘 동안 시각이 지난 글 ${n}개는 초안으로 돌렸다. 다시 시각을 잡는다.` : ''
}

/** Writes an app. Every field travels together (the endpoint replaces the
 *  row), so a patch is merged over the latest copy first. */
export function useUpdateApp() {
  const invalidate = useInvalidateRedeem()
  const toast = useToast()
  return useMutation({
    mutationFn: (v: { app: RedeemApp; patch: Partial<RedeemApp> }) =>
      api.updateRedeemApp(v.app.id, { ...v.app, ...v.patch }),
    onSuccess: (r) => {
      if (r.unslotted) toast.success(`다시 돌린다.${unslottedNote(r.unslotted)}`)
      return invalidate()
    },
    onError: (e) => toast.error(errorText(e)),
  })
}

/** What each state does, in one line. It matches the server: a row that is not
 *  active hands out no code and posts nothing; requests keep coming in. */
export const STATUS_HELP = '멈춤 = 코드도 글도 나가지 않음, 보관 = 멈춤과 같고 목록 아래로'

/** Pausing is the brake on the whole pool, so it sits on the row where the
 *  pool is read. */
export function AppStatusSelect({ app, className }: { app: RedeemApp; className?: string }) {
  const update = useUpdateApp()
  const toast = useToast()
  return (
    <Stop>
      <Select
        aria-label={`${app.name} 상태`}
        value={app.status}
        disabled={update.isPending}
        onChange={(e) => {
          const status = e.target.value as RedeemAppStatus
          update.mutate(
            { app, patch: { status } },
            { onSuccess: () => toast.success(`${app.name} 상태를 ${STATUS.app[status].label}으로 바꿨다`) },
          )
        }}
        title={STATUS_HELP}
        className={cn('h-8 w-24 text-table', app.status === 'paused' && TONE_CLASS.publishing.text, className)}
      >
        {(Object.keys(STATUS.app) as RedeemAppStatus[]).map((s) => (
          <option key={s} value={s}>
            {STATUS.app[s].label}
          </option>
        ))}
      </Select>
    </Stop>
  )
}

/** Turning automatic sending on is asked first (a code that reached the wrong
 *  person cannot be recalled); turning it off is not. The header switch and
 *  the settings card share this one flow. */
function useAutoSend(app: RedeemApp) {
  const update = useUpdateApp()
  const toast = useToast()
  const [asking, setAsking] = useState(false)
  const on = app.auto_handout === 1
  const set = (next: boolean) =>
    next ? setAsking(true) : update.mutate({ app, patch: { auto_handout: 0 } }, { onSuccess: () => toast.success('자동 발송을 껐다') })
  const dialog = (
    <ConfirmDialog
      open={asking}
      onOpenChange={setAsking}
      title={`${app.name} 자동 발송을 켤까?`}
      description="켜면 요청이 오는 대로 코드가 나가고 DM과 공개 답글이 붙는다. 잘못 나간 코드는 되돌릴 수 없다."
      confirmLabel="켜기"
      loading={update.isPending}
      onConfirm={async () => {
        await update.mutateAsync({ app, patch: { auto_handout: 1 } })
        toast.success('자동 발송을 켰다')
        setAsking(false)
      }}
    />
  )
  return { on, set, pending: update.isPending, dialog }
}

/** Whether the app hands out by itself, next to its status. 가동 alone read as
 *  yes: Dazzle Cam ran 가동 with sending off on 2026-10-18, and two requests
 *  waited for a person who thought the desk was sending. Off on a running
 *  app is the warning tone. */
export function AutoSendSwitch({ app, className }: { app: RedeemApp; className?: string }) {
  const s = useAutoSend(app)
  const running = app.status === 'active' && !app.ended_at
  const tone = s.on ? 'published' : running ? 'publishing' : 'neutral'
  return (
    <>
      <button
        type="button"
        role="switch"
        aria-checked={s.on}
        disabled={s.pending}
        onClick={() => s.set(!s.on)}
        title={
          s.on
            ? '요청이 오면 3분 안에 코드 발급, DM, 회신까지 나간다. 누르면 끈다.'
            : '요청은 대기열에 쌓이고 사람이 보낸다. 누르면 켠다.'
        }
        className={cn(
          'inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border px-3 text-table font-medium disabled:opacity-60',
          TONE_CLASS[tone].soft,
          TONE_CLASS[tone].text,
          TONE_CLASS[tone].border,
          className,
        )}
      >
        <StatusDot tone={tone} />
        {s.on ? '자동 발송 켜짐' : '자동 발송 꺼짐'}
      </button>
      {s.dialog}
    </>
  )
}

/** The account types a giveaway can run on: the desk posts and DMs through
 *  one account, and only the Reddit browser session can send DMs (the server
 *  refuses the rest). */
export const DM_PLATFORMS: string[] = ['reddit_browser']

/** The three settings that decide how much of the handout runs by itself.
 *  Automatic sending starts off: a code that reached the wrong person cannot
 *  be recalled. */
export function DeskSettings({ app }: { app: RedeemApp }) {
  const targets = useTargets().data ?? []
  const update = useUpdateApp()
  const toast = useToast()
  const [cap, setCap] = useState(String(app.daily_cap || 20))
  const auto = useAutoSend(app)
  const [showSamples, setShowSamples] = useState(false)
  const samples = useQuery({
    queryKey: ['redeemPreview', app.id],
    queryFn: () => api.redeemMessagePreview(app.id, 3),
    enabled: showSamples,
    gcTime: 0,
  })

  const save = (patch: Partial<RedeemApp>, done?: string) =>
    update.mutate({ app, patch }, { onSuccess: () => done && toast.success(done) })
  const commitCap = () => {
    const n = Math.floor(Number(cap))
    if (!Number.isFinite(n) || n < 1) {
      toast.error('하루 상한은 1장 이상이다. 상한을 없앨 수는 없다.')
      setCap(String(app.daily_cap || 20))
      return
    }
    if (n !== (app.daily_cap || 20)) save({ daily_cap: n }, '일일 상한을 바꿨다')
  }

  return (
    <div className="space-y-4">
      <Field label="게시·DM 계정" hint="이 앱이 글을 올리고 DM을 보낼 때 쓰는 계정. DM을 보낼 수 있는 Reddit 브라우저 세션 계정만 고를 수 있다.">
        <Select
          value={String(app.target_id ?? 0)}
          disabled={update.isPending}
          onChange={(e) => save({ target_id: Number(e.target.value) })}
        >
          <option value="0">계정 미지정</option>
          {targets
            .filter((t) => DM_PLATFORMS.includes(t.platform) || t.id === app.target_id)
            .map((t) => {
              const can = DM_PLATFORMS.includes(t.platform)
              return (
                <option key={t.id} value={String(t.id)} disabled={!can}>
                  {can ? t.name : `${t.name} (DM을 보낼 수 없다)`}
                </option>
              )
            })}
        </Select>
      </Field>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <div className="text-meta font-medium">자동 발송</div>
          <label className="flex items-center gap-2 text-body">
            <input
              type="checkbox"
              className="h-4 w-4 accent-[rgb(var(--accent))]"
              checked={auto.on}
              disabled={auto.pending}
              onChange={(e) => auto.set(e.target.checked)}
            />
            {auto.on ? '켜짐' : '꺼짐'}
          </label>
          <p className="text-meta text-muted-foreground">
            켜면 3분마다 요청을 스스로 처리한다: 코드 발급, DM, 요청 댓글에 회신. 끄면 요청이 큐에 쌓이고 사람이 보낸다.
          </p>
        </div>
        <Field label="하루 발급 상한" hint="지난 24시간에 내보낼 수 있는 코드 수. 자정에 0으로 돌아가지 않고 계속 이어서 센다. 최소 1장이고 0은 쓸 수 없다.">
          <Input
            type="number"
            min={1}
            value={cap}
            disabled={update.isPending}
            onChange={(e) => setCap(e.target.value)}
            onBlur={commitCap}
            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
          />
        </Field>
      </div>

      <div className="space-y-2">
        <Button size="sm" onClick={() => setShowSamples((v) => !v)} title="코드 자리에 자리표를 넣은 샘플. 실제 코드는 쓰이지 않는다.">
          {showSamples ? 'DM 문구 닫기' : 'DM 문구 보기'}
        </Button>
        {showSamples && (
          <div className="space-y-2">
            {samples.isPending && <p className="text-meta text-muted-foreground">불러오는 중</p>}
            {samples.error && <p className="text-meta text-status-failed">{errorText(samples.error)}</p>}
            {samples.data && (samples.data.items ?? []).length === 0 && (
              <p className="text-meta text-muted-foreground">샘플이 없다</p>
            )}
            {(samples.data?.items ?? []).map((s, i) => (
              <div key={i} className="rounded-md border border-border bg-background p-3">
                <div className="text-meta font-medium text-muted-foreground">{s.subject}</div>
                <pre className="mt-1 whitespace-pre-wrap font-sans text-table">{s.body}</pre>
              </div>
            ))}
            {samples.data && (
              <p className="text-meta text-muted-foreground">
                문구는 매번 바뀐다. 위는 예시 세 가지다. DM과 공개 답글은 영어로만 나간다.
              </p>
            )}
          </div>
        )}
      </div>

      {auto.dialog}
    </div>
  )
}

const BLANK: CreateRedeemAppPayload = {
  slug: '',
  name: '',
  platform: 'ios',
  product_name: '',
  product_id: '',
  store_id: '',
  expires_on: '',
}

/** "새 앱": the pool's identity. Everything else is set on the app's own page. */
export function NewAppDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate()
  const invalidate = useInvalidateRedeem()
  const toast = useToast()
  const [form, setForm] = useState<CreateRedeemAppPayload>(BLANK)
  const create = useMutation({
    mutationFn: (p: CreateRedeemAppPayload) => api.createRedeemApp(p),
    onSuccess: async (app) => {
      await invalidate()
      toast.success(`${app.name}을(를) 만들었다`)
      setForm(BLANK)
      onClose()
      navigate(`/redeem/${app.id}/codes`)
    },
  })
  const set = (k: keyof CreateRedeemAppPayload) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }))
  const web = form.platform === 'web'
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="새 앱"
      description="앱을 만든 뒤 App Store Connect·Play에서 받은 코드 CSV를 올린다."
      size="lg"
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          <Button
            variant="primary"
            loading={create.isPending}
            disabled={!form.slug.trim() || !form.name.trim()}
            onClick={() => create.mutate(form)}
          >
            만들기
          </Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="이름" required>
          <Input value={form.name} onChange={set('name')} />
        </Field>
        <Field label="슬러그" hint="소문자. 예: wobble" required>
          <Input value={form.slug} onChange={set('slug')} />
        </Field>
        <Field label="플랫폼">
          <Select value={form.platform} onChange={(e) => setForm((f) => ({ ...f, platform: e.target.value as RedeemPlatform }))}>
            <option value="ios">iOS</option>
            <option value="mac">Mac</option>
            <option value="play">Play</option>
            <option value="web">Web — 자체 사이트, 공유 할인 코드를 DM으로</option>
          </Select>
        </Field>
        {web ? (
          <Field label="사이트 주소" hint="예: https://noveldesk.app">
            <Input value={form.store_url ?? ''} onChange={set('store_url')} />
          </Field>
        ) : (
          <Field label="스토어 id" hint="Apple 앱 id 또는 Play 패키지 이름">
            <Input value={form.store_id} onChange={set('store_id')} />
          </Field>
        )}
        <Field label="해제되는 것" hint="코드로 열리는 것. 예: Wobble Pro">
          <Input value={form.product_name} onChange={set('product_name')} />
        </Field>
        <Field label="상품 id">
          <Input value={form.product_id} onChange={set('product_id')} />
        </Field>
        <Field label="코드 만료일" hint="YYYY-MM-DD">
          <Input value={form.expires_on} onChange={set('expires_on')} placeholder="2026-12-31" />
        </Field>
      </div>
      {create.error && <p className="mt-3 text-table text-status-failed">{errorText(create.error)}</p>}
    </Dialog>
  )
}

/** A titled card: the unit every workspace tab is made of. */
export function Section({
  title,
  hint,
  actions,
  children,
  className,
}: {
  title: ReactNode
  hint?: ReactNode
  actions?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section className={cn('space-y-3 rounded-lg border border-border bg-card p-4', className)}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-section font-semibold">{title}</h2>
          {hint && <p className="mt-0.5 text-meta text-muted-foreground">{hint}</p>}
        </div>
        {actions}
      </div>
      {children}
    </section>
  )
}
